import React, { useState, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import DeleteIcon from "../../assets/images/icons/delete-w.svg";
import viewIcon from "../../assets/images/icons/eye.svg";
import { Link } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../utils/constants";
import editImage from "../../assets/images/icons/edit-2.svg";
import Modal from "../../commons/Modal/Modal";

function PartnerStreamsManagement() {
  const [loading, setLoading] = useState(false);
  const [streams, setStreams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10); // Default limit per page
  const [totalPages, setTotalPages] = useState(1);
  const [logoutModal, setLogoutModal] = useState({ value: false });
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Helper function to construct full video URL if it's a relative path
  const getVideoUrl = (videoPath) => {
    if (!videoPath) return null;
    // If it's already a full URL (starts with http:// or https://), return as is
    if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
      return videoPath;
    }
    // If it's a relative path, construct full URL using API base URL
    const apiBaseUrl = process.env.REACT_APP_API?.replace('/v1', '').replace('/admin', '') || '';
    // Remove trailing slash from base URL and leading slash from path
    const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    const path = videoPath.startsWith('/') ? videoPath : `/${videoPath}`;
    return `${baseUrl}${path}`;
  };

  const getStreams = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", limit.toString());
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const response = await handleApiCall("get", `${endpoints.getPartnerStreamList}?${params.toString()}`);
      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        // Handle the actual API response structure
        const responseData = response?.data?.data;
        // API returns data in partnerStream array
        const streamsData = Array.isArray(responseData?.partnerStream) 
          ? responseData.partnerStream 
          : Array.isArray(responseData?.data) 
          ? responseData.data 
          : Array.isArray(responseData) 
          ? responseData 
          : [];
        
        setStreams(streamsData);
        // Pagination is directly in responseData
        setTotalPages(
          responseData?.totalPages || 
          responseData?.pagination?.totalPages || 
          response?.data?.pagination?.totalPages ||
          1
        );
      } else if (response?.error) {
        // Error already handled by handleApiCall
        console.error("Error fetching streams:", response?.message);
        setStreams([]); // Set empty array on error
      } else {
        setStreams([]); // Set empty array if response structure is unexpected
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
      setStreams([]); // Set empty array on error
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchTerm]);

  useEffect(() => {
    getStreams();
  }, [getStreams]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusToggle = async (itemId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const response = await handleApiCall("put", endpoints.updatePartnerStreamStatus, {
        partner_stream_id: itemId,
        status: newStatus,
      });

      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success("Stream status updated successfully!");
        getStreams(); // Refresh the list
      } else if (response?.error) {
        // Error already handled by handleApiCall
        console.error("Error updating stream status:", response?.message);
      }
    } catch (error) {
      console.error("Error updating stream status:", error);
      // Network errors are already handled by handleApiCall
    }
  };

  const handleDeleteClick = (itemId) => {
    setItemToDelete(itemId);
    setLogoutModal({ value: true });
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      // TODO: Replace with actual API endpoint when available
      // const response = await handleApiCall("delete", `${endpoints.deletePartnerStream}/${itemToDelete}`);

      // if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
      //   toast.success("Stream deleted successfully!");
      //   setLogoutModal({ value: false });
      //   setItemToDelete(null);
      //   getStreams();
      // }
      toast.info("Delete API not yet implemented");
      setLogoutModal({ value: false });
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting stream:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setLogoutModal({ value: false });
    setItemToDelete(null);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Partner Streams Management</h3>
              <Link to="/add-partner-stream" className="btn btn-primary rounded-2 px-3 ml-auto">
                {UI_CONSTANTS.BUTTON_LABELS.CREATE_NEW} <span className="ml-2">+</span>
              </Link>
            </div>

            {/* Search Bar */}
            {/* <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control search-icon left-icon border"
                    placeholder={UI_CONSTANTS.PLACEHOLDERS.SEARCH}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div> */}

            <div className="table-responsive px-0">
              <table className="table table-striped trim">
                <thead>
                  <tr>
                    <th width="80">{UI_CONSTANTS.TABLE_HEADERS.SR_NO}</th>
                    <th width="150">{UI_CONSTANTS.TABLE_HEADERS.NAME}</th>
                    <th>Type</th>
                    <th>Media</th>
                    <th>Order By</th>
                    <th>{UI_CONSTANTS.TABLE_HEADERS.STATUS}</th>
                    <th className="text-center">{UI_CONSTANTS.TABLE_HEADERS.ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                        </div>
                      </td>
                    </tr>
                  ) : !Array.isArray(streams) || streams.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                      </td>
                    </tr>
                  ) : (
                    streams.map((item, index) => {
                      // Handle different ID field names
                      const itemId = item._id || item.id || item.partner_stream_id || `stream-${index}`;
                      const type = (item.type || "").toLowerCase();
                      const isImage = type === "image";
                      const videoPath = item.video || item.video_url || item.videoUrl || null;
                      const videoUrl = getVideoUrl(videoPath);
                      const desktopImagePath = item.desktop_image || item.desktopImage || null;
                      const mobileImagePath = item.mobile_image || item.mobileImage || null;
                      const desktopImageUrl = desktopImagePath ? getVideoUrl(desktopImagePath) : null;
                      const mobileImageUrl = mobileImagePath ? getVideoUrl(mobileImagePath) : null;
                      const title = item.title_en || item.title_english || item.title || "";
                      const status = item.status !== undefined ? item.status : 0;
                      const order_no = item.order_no
                      
                      return (
                        <tr key={itemId}>
                          <td>{(currentPage - 1) * limit + index + 1}</td>
                          <td className="text-center">{title || UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}</td>
                          <td className="text-center">
                            <span className="badge badge-primary">{isImage ? "Image" : "Video"}</span>
                          </td>
                          <td>
                            <div className="sm-img">
                              {isImage ? (
                                desktopImageUrl ? (
                                  <img
                                    src={desktopImageUrl}
                                    alt="Desktop"
                                    style={{
                                      width: "100px",
                                      height: "60px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <span style={{ color: "#999" }}>No image</span>
                                )
                              ) : videoUrl ? (
                                <video
                                  src={videoUrl}
                                  style={{
                                    width: "100px",
                                    height: "60px",
                                    objectFit: "cover",
                                  }}
                                  controls
                                />
                              ) : (
                                <span style={{ color: "#999" }}>No video</span>
                              )}
                            </div>
                          </td>
                          <td>{order_no}</td>
                          <td>
                            <label className="custom-control custom-switch p-1 ml-4 mb-0 mt-1" role="button">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                checked={status === 1}
                                onChange={() => handleStatusToggle(itemId, status)}
                              />
                              <span className="custom-control-label custom-toggle" />
                            </label>
                          </td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              <Link to={`/view-partner-stream/${itemId}`} className="btn btn-link p-0 mx-2" title={UI_CONSTANTS.BUTTON_LABELS.VIEW || "View"}>
                                <img src={viewIcon} alt={UI_CONSTANTS.BUTTON_LABELS.VIEW || "View"} style={{ width: 20, height: 20, filter: "brightness(0) invert(1)", opacity: 0.95 }} className="mr-2" />
                              </Link>
                              <Link to={`/edit-partner-stream/${itemId}`} className="btn btn-link p-0 mx-2">
                                <img src={editImage} alt={UI_CONSTANTS.BUTTON_LABELS.EDIT} className="mr-2" />
                              </Link>
                              {/* <button className="btn btn-link p-0 mx-2" onClick={() => handleDeleteClick(itemId)}>
                                <img src={DeleteIcon} alt={UI_CONSTANTS.BUTTON_LABELS.DELETE} />
                              </button> */}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Page navigation">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        {UI_CONSTANTS.BUTTON_LABELS.PREVIOUS || "Previous"}
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        {UI_CONSTANTS.BUTTON_LABELS.NEXT || "Next"}
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal show={logoutModal.value} onClose={handleDeleteCancel} style={{ zIndex: "1000" }} className=" shadow-none">
        <div className="modal-dialog modal-md modal-center" role="document">
          <div className="modal-content bg-black border-light">
            <div className="modal-body text-center my-4 py-4">
              <h4 className="text-white">
                Do you want to
                <br />
                Delete this stream?
              </h4>
              <div className="col-sm-12 mx-auto d-flex align-items-center mt-4 row">
                <div className="col-6 px-2">
                  <button type="button" className="btn btn-primary btn-lg btn-block font-16 rounded-pill" onClick={handleDeleteConfirm} disabled={deleting}>
                    {deleting ? "Deleting..." : "Yes"}
                  </button>
                </div>
                <div className="col-6 px-2">
                  <button type="button" className="btn btn-default btn-lg btn-block font-16 rounded-pill" onClick={handleDeleteCancel} disabled={deleting}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PartnerStreamsManagement;

